import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { format, addDays, startOfMonth, endOfMonth, startOfWeek, addMonths, subMonths, isSameDay, isSameMonth, isBefore, isToday } from 'date-fns';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { Check } from 'lucide-react';
import { isFirmLike } from '../../lib/orgTypes';

const TYPE_LABELS = { law_firm: 'Law Firm', marketer: 'Marketer', medical_practice: 'Medical Practice', insurance_company: 'Insurance', expert_service: 'Expert Service' };
const TYPE_COLORS = { law_firm: '#F59E0B', marketer: '#8B5CF6', medical_practice: '#10B981', insurance_company: '#3B82F6', expert_service: '#223368' };

export default function OrgProfile() {
  const { id } = useParams();
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const backPath = location.pathname.startsWith('/client') ? '/client/directory' : '/app/directory';
  const initialTab = searchParams.get('tab') || 'overview';

  const slotsRef = useRef(null);
  const confirmRef = useRef(null);

  const [org, setOrg] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [formsAssigned, setFormsAssigned] = useState(0);
  const [activeTab, setActiveTab] = useState(initialTab);

  const fetchOrg = useCallback(async () => {
    try {
      const data = await api.getDirectory({}, currentOrg?.id);
      const found = data.find(o => o.id === id);
      setOrg(found || null);
    } catch (err) {
      console.error('Failed to fetch org:', err);
    } finally {
      setLoading(false);
    }
  }, [id, currentOrg]);

  useEffect(() => { fetchOrg(); }, [fetchOrg]);

  useEffect(() => {
    if (!org) return;
    if (org.type === 'medical_practice') {
      api.getDirectoryProviders(org.id)
        .then(data => setProviders(data))
        .catch(() => setProviders([]));
    }
  }, [org]);

  useEffect(() => {
    if (!selectedDate || !org) { setSlots([]); return; }
    setSlotsLoading(true);
    setSelectedSlot(null);

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const generated = [];
    const day = new Date(`${dateStr}T08:00:00`);
    const end = new Date(`${dateStr}T18:00:00`);
    const now = new Date();
    for (let t = new Date(day); t < end; t = new Date(t.getTime() + 30 * 60000)) {
      if (t > now) {
        generated.push({
          starts_at: t.toISOString(),
          ends_at: new Date(t.getTime() + 30 * 60000).toISOString(),
          available: true,
        });
      }
    }
    setSlots(generated);
    setSlotsLoading(false);
    // Auto-scroll to time slots on mobile
    setTimeout(() => slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, [selectedDate, org]);

  const handleBook = async () => {
    if (!selectedSlot || !org) return;
    setBooking(true);
    try {
      const result = await api.bookAppointment({
        provider_id: selectedProvider?.user_id || null,
        org_id: org.id,
        starts_at: selectedSlot.starts_at,
        ends_at: selectedSlot.ends_at,
        location_name: org?.address_line1 || null,
        type: 'consultation',
      });
      setFormsAssigned(result?.onboarding_forms_assigned || 0);
      setBookingSuccess(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setBooking(false);
    }
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';
  const minDate = format(addDays(new Date(), 1), 'yyyy-MM-dd');

  const mapsUrl = (address) => {
    const encoded = encodeURIComponent(address);
    // iOS → Apple Maps, else Google Maps
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    return isIOS ? `maps://maps.apple.com/?q=${encoded}` : `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  };

  if (loading) {
    return (
      <div className="flex-1 bg-primary">
        <div className="h-[200px] bg-tertiary animate-pulse" />
        <div className="max-w-[960px] mx-auto px-4 md:px-7 py-6 space-y-4">
          <div className="h-8 w-64 bg-tertiary rounded animate-pulse" />
          <div className="h-4 w-40 bg-tertiary rounded animate-pulse" />
          <div className="grid grid-cols-3 gap-4 mt-6">
            {[1,2,3].map(i => <div key={i} className="h-24 bg-tertiary rounded-xl animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="flex-1 bg-primary flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-[18px] font-bold text-primary-c mb-2">Organization not found</h2>
          <button onClick={() => navigate(backPath)} className="text-[13px] text-accent-light hover:underline">Back to Directory</button>
        </div>
      </div>
    );
  }

  const color = TYPE_COLORS[org.type] || '#6B7280';
  const initials = org.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const addr = [org.address_line1, org.city, org.state, org.zip].filter(Boolean).join(', ');
  const isMedical = org.type === 'medical_practice';
  // Who's looking decides the call to action: patients book, law firms refer
  // a case (the concierge opens with this provider chosen), clinics and
  // firms looking at firms just see the profile.
  const isClientView = location.pathname.startsWith('/client');
  const viewerIsFirm = isFirmLike(currentOrg?.type);
  const canBook = isMedical && isClientView;
  const canRefer = isMedical && viewerIsFirm && !isClientView && org.id !== currentOrg?.id;
  const referHref = `/app/referrals/concierge?provider=${org.id}&return=${encodeURIComponent(`/app/directory/${org.id}`)}`;

  return (
    <div className="flex-1 bg-primary">
      {/* Hero Banner */}
      <div className="relative h-[160px] sm:h-[180px] md:h-[220px] overflow-hidden" style={{ background: `linear-gradient(135deg, ${color}dd, ${color}88)` }}>
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(0,0,0,0.5)] to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-[960px] mx-auto px-4 md:px-7 pb-4 sm:pb-5">
          <button onClick={() => navigate(backPath)} className="text-[12px] text-white/70 hover:text-white transition-colors mb-2 sm:mb-3 block">
            &larr; Back to Directory
          </button>
          <div className="flex items-end gap-3 sm:gap-4">
            {org.logo_url ? (
              <img src={org.logo_url} alt="" className="w-[52px] h-[52px] sm:w-[72px] sm:h-[72px] rounded-[12px] sm:rounded-[16px] object-cover border-2 sm:border-[3px] border-white shadow-lg flex-shrink-0" />
            ) : (
              <div className="w-[52px] h-[52px] sm:w-[72px] sm:h-[72px] rounded-[12px] sm:rounded-[16px] flex items-center justify-center text-[18px] sm:text-[24px] font-bold text-white border-2 sm:border-[3px] border-white/30 shadow-lg flex-shrink-0" style={{ background: color }}>
                {initials}
              </div>
            )}
            <div className="pb-0.5 min-w-0">
              <h1 className="text-[18px] sm:text-[24px] md:text-[28px] font-extrabold text-white tracking-tight drop-shadow-lg truncate">{org.name}</h1>
              <div className="flex items-center gap-2.5 mt-1">
                <span className="text-[11px] font-semibold px-2.5 py-[3px] rounded-full text-white/90 bg-white/20 backdrop-blur-sm">
                  {TYPE_LABELS[org.type] || org.type}
                </span>
                {org.partnership?.partnership_status === 'accepted' && (
                  <span className="text-[11px] font-semibold px-2.5 py-[3px] rounded-full text-white bg-[rgba(16,185,129,0.7)] backdrop-blur-sm">
                    Network Partner
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[960px] mx-auto px-4 md:px-7">
        {/* Tab bar */}
        <div className="flex gap-0 border-b border-theme -mx-4 md:-mx-7 px-4 md:px-7 mt-1">
          {[
            { key: 'overview', label: 'Overview' },
            ...(org.locations?.length ? [{ key: 'locations', label: `Locations (${org.locations.length})` }] : []),
            ...(canBook ? [{ key: 'book', label: 'Book Appointment' }] : []),
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 text-[13px] font-semibold transition-all border-b-2 ${
                activeTab === tab.key
                  ? 'text-accent-light border-accent'
                  : 'text-muted-c border-transparent hover:text-primary-c'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="py-6 grid grid-cols-1 md:grid-cols-[1fr_300px] gap-6">
            {/* Left column */}
            <div className="space-y-6">
              {/* Quick info cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {addr && (
                  <a href={mapsUrl(addr)} target="_blank" rel="noopener noreferrer" className="bg-elevated rounded-xl border border-theme p-4 block hover:bg-hover-surface transition-all">
                    <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1.5">Location</div>
                    <p className="text-[13px] text-accent-light font-medium leading-snug hover:underline">{addr}</p>
                  </a>
                )}
                {org.phone && (
                  <div className="bg-elevated rounded-xl border border-theme p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1.5">Phone</div>
                    <a href={`tel:${org.phone}`} className="text-[13px] text-accent-light font-medium hover:underline">{org.phone}</a>
                  </div>
                )}
                {org.email && (
                  <div className="bg-elevated rounded-xl border border-theme p-4">
                    <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-1.5">Email</div>
                    <a href={`mailto:${org.email}`} className="text-[13px] text-accent-light font-medium hover:underline truncate block">{org.email}</a>
                  </div>
                )}
              </div>

              {/* About */}
              {org.description && (
                <div>
                  <h3 className="text-[15px] font-bold text-primary-c mb-2">About</h3>
                  <p className="text-[13px] text-secondary-c leading-relaxed">{org.description}</p>
                </div>
              )}

              {/* Specialties */}
              {org.specialties?.length > 0 && (
                <div>
                  <h3 className="text-[15px] font-bold text-primary-c mb-2.5">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {org.specialties.map(s => (
                      <span key={s} className="text-[12px] font-medium px-3.5 py-1.5 rounded-full bg-accent-glow text-accent-light border border-accent-light/10">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Provider count */}
              {providers.length > 0 && (
                <div className="bg-elevated rounded-xl border border-theme p-4 flex items-center justify-between">
                  <div>
                    <div className="text-[15px] font-bold text-primary-c">{providers.length} Provider{providers.length !== 1 ? 's' : ''}</div>
                    <div className="text-[12px] text-muted-c mt-0.5">{canBook ? 'Available for appointments' : 'On the calendar at this practice'}</div>
                  </div>
                  {canBook && (
                    <button
                      onClick={() => setActiveTab('book')}
                      className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
                    >
                      Book Now
                    </button>
                  )}
                  {canRefer && (
                    <button
                      onClick={() => navigate(referHref)}
                      className="px-4 py-2 rounded-[10px] text-[12px] font-semibold text-white gradient-accent shadow-glow hover:-translate-y-0.5 transition-all"
                    >
                      Refer a case
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Right sidebar — actions */}
            <div className="space-y-4">
              {/* CTA card — patients book; law firms refer a case */}
              {canBook && (
                <div className="bg-elevated rounded-xl border border-theme p-5">
                  <h4 className="text-[14px] font-bold text-primary-c mb-1.5">Book an Appointment</h4>
                  <p className="text-[12px] text-muted-c mb-4">Choose a provider and pick a time that works for you.</p>
                  <button
                    onClick={() => setActiveTab('book')}
                    className="w-full py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
                  >
                    Schedule Now
                  </button>
                </div>
              )}
              {canRefer && (
                <div className="bg-elevated rounded-xl border border-theme p-5">
                  <h4 className="text-[14px] font-bold text-primary-c mb-1.5">Refer a case here</h4>
                  <p className="text-[12px] text-muted-c mb-4">Pick the client, the packet assembles from the case file, and {org.name} has one hour to accept. Your client then books from their open slots.</p>
                  <button
                    onClick={() => navigate(referHref)}
                    className="w-full py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
                  >
                    Refer a case
                  </button>
                </div>
              )}

              {/* Contact card */}
              <div className="bg-elevated rounded-xl border border-theme p-5 space-y-3">
                <h4 className="text-[14px] font-bold text-primary-c">Contact</h4>
                {org.phone && (
                  <a href={`tel:${org.phone}`} className="flex items-center gap-2.5 text-[13px] text-secondary-c hover:text-accent-light transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    {org.phone}
                  </a>
                )}
                {org.email && (
                  <a href={`mailto:${org.email}`} className="flex items-center gap-2.5 text-[13px] text-secondary-c hover:text-accent-light transition-colors truncate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {org.email}
                  </a>
                )}
                {org.website && (
                  <a href={org.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 text-[13px] text-secondary-c hover:text-accent-light transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                    Website
                  </a>
                )}
                {addr && (
                  <a href={mapsUrl(addr)} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2.5 text-[13px] text-secondary-c hover:text-accent-light transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 flex-shrink-0"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                    <span className="hover:underline">{addr}</span>
                  </a>
                )}
              </div>

              {/* Partnership status */}
              {org.partnership && (
                <div className="bg-elevated rounded-xl border border-theme p-5">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${org.partnership.partnership_status === 'accepted' ? 'bg-success' : 'bg-warning'}`} />
                    <span className="text-[12px] font-semibold text-primary-c">
                      {org.partnership.partnership_status === 'accepted' ? 'Network Partner' : 'Partnership Pending'}
                    </span>
                  </div>
                </div>
              )}

            </div>
          </div>
        )}

        {/* Book Appointment Tab */}
        {/* Locations Tab */}
        {activeTab === 'locations' && (
          <div className="py-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(org.locations || []).map(l => {
                const laddr = [l.address_line1, l.address_line2, l.city, l.state, l.zip].filter(Boolean).join(', ');
                return (
                  <div key={l.id} className="bg-elevated rounded-xl border border-theme p-5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[14px] font-bold text-primary-c">{l.name}</div>
                      {l.is_primary && (
                        <span className="text-[10px] font-semibold px-2 py-[3px] rounded-full bg-accent-glow text-accent-light">Primary</span>
                      )}
                    </div>
                    {laddr ? (
                      <a href={mapsUrl(laddr)} target="_blank" rel="noopener noreferrer" className="text-[13px] text-accent-light font-medium leading-snug hover:underline block mb-1.5">
                        {laddr}
                      </a>
                    ) : (
                      <p className="text-[13px] text-muted-c mb-1.5">No address on file</p>
                    )}
                    {l.phone && (
                      <a href={`tel:${l.phone}`} className="text-[12px] text-secondary-c hover:text-accent-light transition-colors">{l.phone}</a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'book' && (() => {
          const today = new Date();
          const monthStart = startOfMonth(calendarMonth);
          const monthEnd = endOfMonth(calendarMonth);
          const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
          const calDays = Array.from({ length: 42 }, (_, i) => addDays(calStart, i));

          return (
            <div className="py-6">
              {bookingSuccess ? (
                <div className="bg-success-soft border border-[rgba(16,185,129,0.3)] rounded-xl p-6 sm:p-8 text-center max-w-[500px] mx-auto">
                  <div className="w-[48px] h-[48px] bg-success-soft text-success rounded-[14px] flex items-center justify-center mx-auto mb-4"><Check size={24} strokeWidth={2.5} /></div>
                  <h3 className="text-[18px] font-bold text-primary-c mb-2">Appointment Requested</h3>
                  <p className="text-[13px] text-secondary-c mb-5 leading-relaxed">
                    Your request has been sent. You'll receive a confirmation email once it's approved.
                    {formsAssigned > 0 && (
                      <>
                        <br />
                        <span className="font-semibold text-primary-c">
                          You have {formsAssigned} onboarding form{formsAssigned !== 1 ? 's' : ''} to complete before your visit.
                        </span>
                      </>
                    )}
                  </p>
                  <div className="flex flex-col sm:flex-row sm:justify-center gap-2.5">
                    {formsAssigned > 0 && (
                      <button
                        onClick={() => navigate('/client/forms')}
                        className="w-full sm:w-auto px-5 py-3 sm:py-2.5 rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all"
                      >
                        Complete Forms Now
                      </button>
                    )}
                    <button
                      onClick={() => { setBookingSuccess(false); setFormsAssigned(0); setSelectedDate(null); setSelectedSlot(null); }}
                      className={`w-full sm:w-auto px-5 py-3 sm:py-2.5 rounded-[10px] text-[13px] font-semibold transition-all ${
                        formsAssigned > 0
                          ? 'text-primary-c bg-tertiary border border-theme hover:bg-hover-surface'
                          : 'text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5'
                      }`}
                    >
                      Book Another
                    </button>
                    <button
                      onClick={() => setActiveTab('overview')}
                      className="w-full sm:w-auto px-5 py-3 sm:py-2.5 rounded-[10px] text-[13px] font-semibold text-primary-c bg-tertiary border border-theme hover:bg-hover-surface transition-all"
                    >
                      Back to Profile
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
                  {/* Calendar */}
                  <div>
                    <div className="bg-elevated rounded-xl border border-theme p-5">
                      {/* Month nav */}
                      <div className="flex items-center justify-between mb-4">
                        <button onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))} className="w-8 h-8 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all text-[14px]">
                          &larr;
                        </button>
                        <h3 className="text-[15px] font-bold text-primary-c">{format(calendarMonth, 'MMMM yyyy')}</h3>
                        <button onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))} className="w-8 h-8 rounded-lg bg-tertiary text-muted-c flex items-center justify-center hover:bg-hover-surface hover:text-primary-c transition-all text-[14px]">
                          &rarr;
                        </button>
                      </div>

                      {/* Day headers */}
                      <div className="grid grid-cols-7 gap-1 mb-1">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                          <div key={d} className="text-center text-[11px] font-semibold text-muted-c py-1.5">{d}</div>
                        ))}
                      </div>

                      {/* Calendar grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {calDays.map((day, i) => {
                          const inMonth = isSameMonth(day, calendarMonth);
                          const isPast = isBefore(day, today) && !isToday(day);
                          const isSel = selectedDate && isSameDay(day, selectedDate);
                          const isTod = isToday(day);
                          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                          const disabled = isPast || !inMonth || isWeekend;

                          return (
                            <button
                              key={i}
                              onClick={() => !disabled && setSelectedDate(day)}
                              disabled={disabled}
                              className={`aspect-square flex items-center justify-center rounded-[10px] text-[13px] font-medium transition-all ${
                                isSel ? 'bg-accent text-white font-bold shadow-glow' :
                                isTod && !isSel ? 'bg-accent-glow text-accent-light font-bold' :
                                disabled ? 'text-muted-c/30 cursor-default' :
                                'text-primary-c hover:bg-hover-surface'
                              }`}
                            >
                              {day.getDate()}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Time slots + summary */}
                  <div className="space-y-4" ref={slotsRef}>
                    {selectedDate ? (
                      <>
                        <div className="bg-elevated rounded-xl border border-theme p-4">
                          <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2">
                            {format(selectedDate, 'EEEE, MMMM d')}
                          </div>
                          {slotsLoading ? (
                            <div className="space-y-1.5">
                              {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="h-9 bg-tertiary rounded-lg animate-pulse" style={{ animationDelay: `${i * 40}ms` }} />
                              ))}
                            </div>
                          ) : slots.length === 0 ? (
                            <p className="text-[12px] text-muted-c py-4 text-center">No available times</p>
                          ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:space-y-1.5 sm:gap-0 max-h-none sm:max-h-[320px] sm:overflow-y-auto">
                              {slots.map(slot => {
                                const isSel = selectedSlot?.starts_at === slot.starts_at;
                                return (
                                  <button
                                    key={slot.starts_at}
                                    onClick={() => { setSelectedSlot(slot); setTimeout(() => confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100); }}
                                    className={`w-full py-3 sm:py-2.5 rounded-lg text-[13px] font-semibold transition-all border ${
                                      isSel
                                        ? 'text-white border-transparent shadow-glow'
                                        : 'bg-tertiary text-secondary-c border-strong hover:bg-hover-surface hover:text-primary-c'
                                    }`}
                                    style={isSel ? { background: '#0DA7CA' } : undefined}
                                  >
                                    {format(new Date(slot.starts_at), 'h:mm a')}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Booking summary */}
                        {selectedSlot && (
                          <div ref={confirmRef} className="bg-elevated rounded-xl border border-theme p-4 animate-fade-in">
                            <div className="text-[10px] font-bold uppercase tracking-[1px] text-muted-c mb-2.5">Confirm</div>
                            <div className="space-y-1.5 mb-3 text-[13px]">
                              <div className="flex justify-between">
                                <span className="text-muted-c">Date</span>
                                <span className="text-primary-c font-medium">{format(selectedDate, 'MMM d, yyyy')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-c">Time</span>
                                <span className="text-primary-c font-medium">{format(new Date(selectedSlot.starts_at), 'h:mm a')}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-c">Practice</span>
                                <span className="text-primary-c font-medium truncate ml-2">{org.name}</span>
                              </div>
                            </div>
                            <button
                              onClick={handleBook}
                              disabled={booking}
                              className="w-full py-3 rounded-[10px] text-[14px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50"
                            >
                              {booking ? 'Booking...' : 'Confirm Appointment'}
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="bg-elevated rounded-xl border border-theme p-5 text-center">
                        <p className="text-[13px] text-muted-c">Select a date to see available times</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
